import { useEffect, useState } from "react";
import { TextPad, hasOpenSheet } from "../shared";
import css from "./BlockReasonPrompt.module.css";

/**
 * Sub-modal triggered by «ЗАБЛОКИРОВАТЬ» button. Per screen-03 эталон
 * (`.cyber-prompt.danger`):
 *   - blue/purple gradient border, centred floating panel
 *   - blue "Заблокировать · причина" title
 *   - single text input (placeholder "Ремонт, корпоратив, личные…", max 80)
 *   - hint "Подсказка попадёт в комментарий ДК."
 *   - actions: ЗАБЛОКИРОВАТЬ blue + ОТМЕНА ghost
 *
 * Replaces the native `window.prompt` we were using as a placeholder. The
 * supplied reason is appended to the booking comment as "БЛОК · <reason>".
 */
export interface BlockReasonPromptProps {
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

const PLACEHOLDER = "Ремонт, корпоратив, личные…";

export function BlockReasonPrompt({ onConfirm, onCancel }: BlockReasonPromptProps) {
  const [value, setValue] = useState("");
  /* БМ5: поле причины — readonly-триггер, ввод через TextPad (без autofocus). */
  const [padOpen, setPadOpen] = useState(false);

  /* Esc cancels — но не пока поверх открыт TextPad (hasOpenSheet). */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !hasOpenSheet()) onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const submit = () => {
    const trimmed = value.trim();
    if (!trimmed) return;
    onConfirm(trimmed);
  };

  return (
    <div
      className={css.overlay}
      onClick={(e) => e.target === e.currentTarget && onCancel()}
      role="presentation"
    >
      <div
        className={css.panel}
        role="dialog"
        aria-modal="true"
        aria-label="Причина блокировки"
      >
        <div className={css.body}>
          <div className={css.title}>Заблокировать · причина</div>

          <input
            type="text"
            readOnly
            inputMode="none"
            data-pad-host="" /* 06.09: хост TextPad-шита */
            className={css.input}
            placeholder={PLACEHOLDER}
            value={value}
            onClick={() => setPadOpen(true)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                submit();
              }
            }}
            aria-label="Причина блокировки"
            autoComplete="off"
            spellCheck={false}
          />

          <div className={css.hint}>Подсказка попадёт в комментарий ДК.</div>

          <div className={css.actions}>
            <button
              type="button"
              className={`${css.btn} ${css.btnDanger}`}
              onClick={submit}
              disabled={!value.trim()}
            >
              ЗАБЛОКИРОВАТЬ
            </button>
            <button
              type="button"
              className={`${css.btn} ${css.btnGhost}`}
              onClick={onCancel}
            >
              ОТМЕНА
            </button>
          </div>
        </div>
      </div>

      {/* БМ6: причина блокировки — TextPad multiline (лимит прежний, 80). */}
      <TextPad
        open={padOpen}
        label="Причина блокировки"
        multiline
        maxLength={80}
        placeholder={PLACEHOLDER}
        value={value}
        onCommit={setValue}
        onClose={() => setPadOpen(false)}
      />
    </div>
  );
}
